<!-- eslint-disable vue/no-v-html -->
<template>
  <MultiStepForm :steps="blocks" :init-step="initStep">
    <!-- Landing step  -->
    <template #landing="{ nextStep }">
      <LandingBlock
        :image="document?.cloudinaryHeroImage ? document?.cloudinaryHeroImage[0]?.url : ''"
        :title="document?.title"
        :subtitle="document?.subtitle"
        :description="document?.description"
        class="md:mb-7"
        :cta="true"
        :theme="THEMES.DJURENS_RATT"
        @on-next="advance('landing', nextStep)"
      />
      <LandingBlock
        :image="
          document?.cloudinaryMotivationImage ? document?.cloudinaryMotivationImage[0]?.url : ''
        "
        :title="document?.motivationTitle"
        :subtitle="document?.motivationSubtitle"
        :description="document?.motivation"
        :reverse="true"
        class="z-10 -mt-4 xl:mb-6 xl:mt-0"
        :theme="THEMES.DJURENS_RATT"
      />
      <!-- TODO: Set this up for 1882 too -->
      <!--<ThirdDonationBlock
        v-if="SITE_ORG === 'djurensratt'"
        :image="document?.cloudinaryMiscImage ? document?.cloudinaryMiscImage[0]?.url : ''"
        :title="document.miscTitle"
        :subtitle="document.miscSubtitle"
        :description="document.miscDescription"
        class="mb-7"
      />-->
    </template>

    <!-- Sum step  -->
    <template #sum>
      <FormWrapper
        :title="$t('form1.sum-step.header')"
        :image="imageUrl"
        :theme="THEMES.DJURENS_RATT"
      >
        <TabbedDonationSelector
          v-model:sum="sum"
          :sums="document.sums ?? []"
          :currency="currency"
          :min-sum="minSum"
          :image="imageUrl"
          :v$="v$.sum as any"
          @on-next="onSumNext"
        />
      </FormWrapper>
    </template>

    <!-- Details step  -->
    <template #details="{ nextStep }">
      <FormWrapper
        :title="$t('form1.personal-details-step.header')"
        :sub-title="$t('personal-details.title')"
        :image="imageUrl"
        :theme="THEMES.DJURENS_RATT"
      >
        <PersonalDetails
          v-model:person="person"
          v-model:masked="masked"
          :site-org="SITE_ORG"
          :v$="v$.personDetails as any"
          @on-next="advance('details', nextStep)"
        />
      </FormWrapper>
    </template>

    <!-- Address step  -->
    <template #address>
      <FormWrapper
        :title="$t('form1.postal-address.header')"
        :sub-title="$t('postal-address.retrieved-data')"
        :image="imageUrl"
        :theme="THEMES.DJURENS_RATT"
      >
        <PostalAddress
          v-model:person="person"
          v-model:masked="masked"
          v-model:save-postal-address="savePostalAddress"
          :next-text="$t('stripe.next-step')"
          :show-city="true"
          :v$="v$.personPostal as any"
          :site-org="SITE_ORG"
          @on-next="handleForm"
        />
        <div
          class="mt-4 w-full font-sans text-sm prose-a:text-blue-600 prose-a:underline xl:max-w-[575px]"
          v-html="document?.integrityReference?.fields.onForm"
        ></div>
      </FormWrapper>
    </template>
  </MultiStepForm>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

import { MultiStepForm, type ISteps, THEMES } from 'refresh-ui'
import { zFormUrls } from 'refresh-common'
import { flagError } from 'error-handling'
import { createPerson } from 'shared-types'
import { useVuelidate } from '@vuelidate/core'
import { useI18n } from 'vue-i18n'
import Personnummer from 'personnummer'
import { LandingBlock, FormWrapper, PersonalDetails, PostalAddress } from 'act-components'
import { createFundraisingTracking } from 'fundraising-ui'
import { cloudinaryStillUrl } from 'act-components/src/libs/cloudinaryVideo'
import { zSsnLookupRes } from '~/libs/apiSchema'
import { getSiteDefaults } from '~/libs/defaults'

import type { SiteLang, SiteOrg, Environment } from '~/types/env'
import type { DonationDocument } from '~/types/cms'
import { useFormLoader } from '~/composables/useFormLoader'
import { usePrefillPerson } from '~/composables/usePrefillPerson'

import { commonRules } from '~/libs/rules'

import TabbedDonationSelector from '@/components/elements/TabbedDonationSelector.vue'
import { usePhoneNumber } from '@/composables/phoneNumber'

import { FormType } from '~/types/forms'
import { useFormErrorHandler } from '~/composables/useFormErrorHandler'
import { contentfulStats } from '~/libs/formStatsField'

const { setLocale, getLocaleCookie } = useI18n()
const config = useRuntimeConfig()
const route = useRoute()
const { $rollbar } = useNuxtApp()
const { isValidPhoneNumber, formatPhoneNumber } = usePhoneNumber()

const { t } = useI18n()
const { showLoader, hasResult } = useFormLoader()
const formStatus = ref('idle') as Ref<'idle' | 'loading' | 'success' | 'error'>
type Currency = 'sek' | 'eur' | 'usd'

interface FormDefaults {
  sums: {
    [key in Currency]: {
      sum: number
      minSum: number
    }
  }
  countryCode: 'SE' | 'UA' | 'EN' | 'ES'
}

/** Handle env vars  */
const { SITE_ORG, SITE_LANG, ENVIRONMENT } = config.public as {
  SITE_ORG: SiteOrg
  SITE_LANG: SiteLang
  ENVIRONMENT: Environment
}

if (SITE_ORG !== '1882')
  throw createError({
    status: 404,
    statusText: t('error.404.org'),
    data: {
      buttonText: t('error.404.altOrg'),
      buttonUrl: '/donation',
    },
  })

/** Handle language override */

const currency: Currency = 'eur'

// Get default slug based on either url lang param or site lang
const { defaultSlug } = getSiteDefaults({
  lang: (route.query?.lang as SiteLang) || SITE_LANG,
  env: ENVIRONMENT,
})

// Use the url slug, or default back to the language/site default slug if not provided
const slug = (route?.params?.slug as string) || defaultSlug
const basePath = route.path.split('/')[1]

/** Set up steps */
const blocks = computed<ISteps>(() => ({
  landing: {
    name: 'landing',
    title: t('form1.step1.title'),
    visible: true,
  },
  sum: {
    name: 'sum',
    title: t('form1.step2.title'),
    visible: true,
  },
  details: {
    name: 'details',
    title: t('form1.step3.title'),
    visible: true,
  },
  address: {
    name: 'address',
    title: t('form1.step4.title'),
    visible: true,
  },
}))

const { initStep: defaultStep, debugSteps, isDebug, isPreview } = useStepNavigation(blocks)
const initStep = ref(defaultStep.value)

/** Get cms data */
const document = await useContentfulData<DonationDocument>(slug, 'gava', isPreview.value, t)

// Check if form is closed
const { checkFormClosed } = useFormStatus()
checkFormClosed(document?.isClosed, basePath)

// Validate and parse query params
// NOTE: ALL query params used must be set up here
// Get query params from url
// Example: http://localhost:3000/donation?fname=em&lname=dahlstr%C3%B6m&birthDate=2010-10-10&sum=10&email=em@animalrights.se&mobile=46738347507&street=hattgatan+1&postcode=12947&countryCode=SE&debug=true&campaignCode=1234&lang=sv
const params = ref(zFormUrls.parse(route.query))

// Negotiate language -- url params then cms then site
const formLang = params.value.lang || document?.lang || SITE_LANG
if (formLang !== getLocaleCookie()) {
  setLocale(formLang)
}

const sum = ref<number>(isDebug.value ? 0.5 : (params.value.sum ?? document?.sums?.[0] ?? 25))

const campaignCode = ref(params.value.campaignCode || document?.campaignCode?.fields.code)

// The same Matomo funnel events as the fundraising-ui flows, sent from this page's own steps.
const tracking = createFundraisingTracking({
  type: 'donation',
  name: `cf:${slug}`,
  campaign: campaignCode,
  org: SITE_ORG,
  lang: formLang,
})
const advance = (step: string, next: () => void) => {
  tracking.track(`step:${step}`)
  next()
}

// Normalize result page references
const resultReferences = {
  ok: document?.tackDirektbetalningOk?.sys.id,
  aborted: document?.fallbackDirektbetalningAvbruten?.sys.id,
}

const imageUrl = document?.cloudinaryHeroImage ? (document?.cloudinaryHeroImage[0]?.url ?? '') : ''

const savePostalAddress = ref(false)

/** Handle url params and related defaults */

const formDefaults = useFormDefaults({
  formType: FormType.DONATION,
  siteOrg: SITE_ORG,
  siteLang: SITE_LANG,
})

const minSum = ref<number>(
  isDebug.value ? 0.5 : Number(document?.minSum) || formDefaults.sums?.minSum || 1,
)
const person = ref(
  createPerson({
    name: {
      first: params.value.fname as string,
      last: params.value.lname as string,
    },
    contacts: { mobile: params.value.mobile as string, email: params.value.email as string },
    address: {
      street: params.value.street as string,
      postcode: params.value.postcode as string,
      city: params.value.city as string,
      countryCode: params.value.countryCode as string,
    },
    birthDate: params.value.birthDate as string,
  }),
)
// Reached from a second ask (`?prefill=1`): prefill from the result-token cookie, not the URL.
usePrefillPerson(person)
const masked = ref(createPerson())

const baseUrl = `${useRequestURL().protocol}//${useRequestURL().hostname}${useRequestURL().port === '80' || useRequestURL().port === '8080' ? '' : `:${useRequestURL().port}`}`

const resultUrls = useResultUrls({
  entity: person,
  resultDocuments: {
    ok: document?.tackDirektbetalningOk,
    aborted: document?.fallbackDirektbetalningAvbruten,
  },
  route,
  slug,
  baseUrl,
  type: 'donation-single',
})

useHead({
  title: document.title,
  meta: [{ name: 'description', content: document.description }],
})

useSeoMeta({
  title: document.title,
  ogTitle: document.title,
  description: document.description,
  ogDescription: document.description,
  ogImage: cloudinaryStillUrl(imageUrl),
  twitterCard: 'summary_large_image',
})

definePageMeta({
  layout: 'form',
  alias: ['/donation', '/donation/:slug(.*)*'],
})

onMounted(async () => {
  route.meta.debugSteps = debugSteps.value
  route.meta.debugUrls = resultUrls.value
  route.meta.formStatus = formStatus

  if (params.value.ssn) {
    if (!Personnummer.valid(params.value.ssn)) {
      // reset state
      masked.value = createPerson()
      person.value.useLookup = false
    } else {
      showLoader.value = true
      const lookupResult = zSsnLookupRes.parse(
        await $fetch('/api/lookup/ssn', { method: 'POST', body: { ssn: params.value.ssn } }),
      )
      hasResult.value = lookupResult.success
      showLoader.value = false
      person.value.useLookup = lookupResult.success
      if (!lookupResult.success) {
        masked.value = createPerson()
        return
      }
      const { name, address } = lookupResult
      masked.value = createPerson({ name, address, contacts: {} })
    }
  }
  if (route.query?.start) {
    type startType = undefined | 'sum' | 'details'
    const start = route.query?.start as startType
    const stepsSkip: Record<string, number> = {
      sum: 1,
      details: 2,
    }
    initStep.value = start ? (stepsSkip[start] ?? 0) : 0
  }
  try {
    // Health check
    const { crmHealthy, integrationMode, settings } = await useCrmHealthCheck()
    if (!crmHealthy || settings.fallbackForms || document.maintenance) {
      integrationMode.value = 'manual'
      manualRedirect()
    }
  } catch (e: any) {
    // TODO: Remove console.log
    // console.log('Health check failed', e?.message || 'Unknown error in health check')
    flagError(e, 'donation/health-check', $rollbar)
  }
})

const payment = ref<string>('card') /* (
  SITE_ORG === '1882' ? 'stripe' : document.paymentTypes[0].fields.key || 'card',
) */

/** Set up form validation */
const rules = computed(() => {
  return commonRules({
    t,
    siteLang: SITE_LANG,
    person: person.value,
    minSum: minSum.value,
    currency,
    includeCity: true,
  })
})

const v$ = useVuelidate(rules, { sum, personDetails: person, personPostal: person })
const { reportError } = useFormErrorHandler()

const handleForm = async () => {
  try {
    v$.value.$touch()
    v$.value.$validate() // NOTE: Is this extra validation call necessary? $touch() should be enough
    if (v$.value.$invalid) {
      // TODO: Remove console.log
      console.error('Form invalid')
      throw new Error('invalid')
    }
    tracking.track('step:address')
    formStatus.value = 'loading'
    tracking.track('submit', sum.value)

    const redirectUrl = await $fetch('/api/stripe/donation-1882', {
      method: 'POST',
      body: {
        name: t('payment.processor.category.donation'),
        amount: isDebug.value ? 0.5 : sum.value,
        currency: 'EUR',
        email: person.value.contacts.email,
        firstName: person.value.name.first,
        lastName: person.value.name.last,
        country: person.value.address.countryCode,
        postalCode: person.value.address.postcode,
        street: person.value.address.street,
        city: person.value.address.city,
        birthDate: person.value.birthDate,
        ssn: person.value.ssn,
        mobile: person.value.contacts.mobile,
        success_url: resultUrls.value.ok,
        cancel_url: resultUrls.value.aborted,
        stats: contentfulStats(slug, formLang, isDebug.value),
        campaignCode: campaignCode.value,
        language: formLang,
      },
    })
    tracking.track('accepted', sum.value)
    return navigateTo(redirectUrl, { external: true })
  } catch (e: any) {
    if (formStatus.value === 'loading') tracking.track('error')
    reportError(e, 'handleForm', { paymentType: payment.value, route: route.fullPath })
  }
}

const manualRedirect = (): void => {
  navigateTo('https://project1882.org/service-maintenance/', { external: true, redirectCode: 302 })
}

const { handleTabNext } = useTabbedDonationNavigation({ blocks, initStep, sum, org: SITE_ORG })
// The monthly tab leaves for the monthly form, which reports its own funnel.
const onSumNext = (type: 'single' | 'monthly') => {
  if (type === 'single') tracking.track('step:amount')
  return handleTabNext(type, '/monthly')
}

watch(
  () => person.value.contacts.mobile,
  async mobile => {
    // Guard from stale async value overwriting newer values
    const inputAtStart = mobile
    if (await isValidPhoneNumber(inputAtStart, formDefaults.countryCode || 'SE')) {
      const formatted = await formatPhoneNumber(inputAtStart, formDefaults.countryCode || 'SE')
      if (person.value.contacts.mobile === inputAtStart) {
        person.value.contacts.mobile = formatted
      }
    }
  },
)
</script>
